import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { useState, useMemo, useEffect } from "react";
import Stepper from "@/components/common/stepper";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Step1 from "@/components/menu-management/special-menu/sunday-roast/add-sunday-roast/step1";
import Step2 from "@/components/menu-management/special-menu/sunday-roast/add-sunday-roast/step2";
import Step3 from "@/components/menu-management/special-menu/sunday-roast/add-sunday-roast/step3";
import useAuth from "@/hooks/useAuth";
import CreateMenuSkeleton from "@/components/skeleton/create-menu-skeleton";
import StepFormButtons from "@/components/common/step-for-button";
import { getFirstErrorMessage } from "@/lib/utils";
import {
  createNewSundayRoast,
  clearSundayRoastMessages,
  updateExistingSundayRoast,
} from "@/redux/slice/menu-manage-slice/special-menu/sunday-roast-slice";
import { toast } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { fetchAllDays } from "@/redux/slice/utility/day-slice";
import { fetchAllFlatMenu } from "@/redux/slice/menu-manage-slice/menu-slice";
import { setItemVariants } from "@/redux/slice/essential-menu-slice/variants-slice";
import { useStepFormHandler } from "@/hooks/useStepFormHandler";

export const menu_id = 15;

function CreateSundayRoast() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { type } = useParams();
  const location = useLocation();
  const stepLabels = ["Step 1", "Step 2", "Step 3"];
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const item = location.state;

  useEffect(() => {
    dispatch(fetchAllDays());
  }, []);

  const { createLoading, error, updateLoading } = useSelector(
    (state) => state.menuManage.sundayRoast
  );
  const { getLoading } = useSelector((state) => state.utility.day);

  const form = useForm({
    resolver: zodResolver(createSundayRoast),
    mode: "onChange",
    defaultValues: {
      type: "create",
      item_no: null,
      name: "",
      description: "",
      vat: 0,
      status: "active",
      day_id: null,
      price: null,
      items: [],
      file_manager_id: undefined,
    },
  });
  const errorMessage = getFirstErrorMessage(form);

  const { step, nextStep, setStep, prevStep, entityId, loading } =
    useStepFormHandler({
      type,
      entity: "Sunday Roast",
      user,
      createAction: createNewSundayRoast,
      updateAction: updateExistingSundayRoast,
      totalSteps: stepLabels.length,
      item: item,
      toastSteps: { 0: false, 1: false, 2: true },
      successRedirectLink: "/backoffice/menu/special-menu/sunday-roast",
    });

  useEffect(() => {
    dispatch(fetchAllFlatMenu(user.restaurant_id));
  }, [user.restaurant_id]);

  useEffect(() => {
    if (type !== "update") return;
    if (!item) {
      navigate("/backoffice/menu/special-menu/sunday-roast");
      return;
    }
    form.reset({
      type: "update",
      item_no: item?.item_no || null,
      name: item?.name || "",
      description: item?.description || "",
      price: item?.price || null,
      day_id: item?.day?.id || null,
      items: item?.items || [],
      vat: item?.vat ?? 0,
      status: item?.status || "active",
      file_manager_id: item?.file_manager_id || undefined,
    });

    if (item?.file_manager_id && item?.file_manager_url != null) {
      setFiles([
        {
          file_manager_id: item?.file_manager_id,
          preview: item?.file_manager_url,
          name: item?.image?.name,
        },
      ]);
    }
    setItemVariants(item?.sunday_roast_items);
  }, [type, item?.id, form]);

  if (getLoading) return <CreateMenuSkeleton />;

  return (
    <Layout2
      pageName={{
        title: `${
          type == "update" ? "Update Sunday Roast" : "Add New Sunday Roast"
        }`,
      }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        {
          name: "Special Menu",
          path: "/backoffice/menu/special-menu/dish-the-day",
        },
        {
          name: "Sunday Roast",
          path: "/backoffice/menu/special-menu/sunday-roast",
        },
        {
          name: `${
            type == "update" ? "Update Sunday Roast" : "Add New Sunday Roast"
          }`,
          path: `/backoffice/menu/special-menu/${type}`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <div className="py-5 container mt-5">
        <Stepper
          steps={stepLabels}
          onStepChange={setStep}
          type={form.watch("type")}
          currentStep={step}
        />
        {errorMessage && (
          <div className="text-cred mb-2 text-sm mt-4 flex justify-center items-center text-center">
            {errorMessage}
          </div>
        )}
        <Form {...form}>
          <form>
            {step === 0 && <Step1 menu_id={menu_id} form={form} />}
            {step === 1 && (
              <Step2
                menu_id={menu_id}
                item_id={entityId || item?.id}
                form={form}
              />
            )}
            {step === 2 && (
              <Step3 files={files} setFiles={setFiles} form={form} />
            )}

            <StepFormButtons
              step={step}
              totalSteps={stepLabels.length}
              isLoading={createLoading || updateLoading || loading}
              type={type}
              status={form.watch("status")}
              cancelLink="/backoffice/menu/special-menu/sunday-roast"
              onBack={prevStep}
              onNext={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form); // Step 1 & Step 2 → draft
              }}
              onSubmit={async () => {
                const data = form.getValues();
                await nextStep(data?.status, data, form); // Step 3 → active
              }}
              onDraftSave={async () => {
                const data = form.getValues();
                await nextStep("draft", data, form, { draftButton: true });
              }}
            />
          </form>
        </Form>
      </div>
    </Layout2>
  );
}

export default CreateSundayRoast;

export const createSundayRoast = z.object({
  item_no: z
    .preprocess(
      (val) => {
        if (val === "" || val == null) return null;
        return Number(val);
      },
      z
        .number({
          invalid_type_error: "Item no must be a number",
        })
        .nullable()
    )
    .optional(),

  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  day_id: z.preprocess((val) => {
    if (val === "" || val == null) return undefined;
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  }, z.number().optional()),

  sunday_roast_items: z.array(z.object({}).catchall(z.any())).optional(),
  price: z.preprocess(
    (val) => (val === "" ? undefined : Number(val)),
    z
      .number({
        required_error: "Price is required",
        invalid_type_error: "Price must be a number",
      })
      .optional()
  ),
  vat: z
    .preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        invalid_type_error: "VAT percentage must be a number",
      })
    )
    .refine((val) => val >= 0, {
      message: "VAT percentage cannot be negative",
    })
    .refine((val) => val <= 100, {
      message: "VAT percentage cannot exceed 100",
    })
    .optional(),
  file_manager_id: z
    .number({
      required_error: "Please upload an image",
      invalid_type_error: "File ID must be a number",
    })
    .int("File ID must be an integer")
    .optional(),
  status: z.enum(["active", "inactive", "draft"]).default("active"),
});
